import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, getDocs, query, orderBy, deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { Plus, Search, Trash2, CreditCard, History, ArrowLeft, MoreVertical, Edit2, Calendar, User, FilterX, Building2, IndianRupee, FileText, CheckCircle, Clock, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import { AccountsInvoiceModal, PaymentModal, InvoicePaymentsDetailModal } from './AccountsModals.js';
import { Link, useLocation } from 'react-router-dom';
import Spinner from './Spinner.js';
import useClickOutside from './hooks/useClickOutside.js';

const SALES_TEAM = [
    "Rajesh Soni", "Sanjay Kumawat", "Deepak Yadav", "Deepak Kumar", 
    "Rohit Soni", "Prinka Sharma", "Ankit Kumar Jangid", "Piyush Agarwal"
];

const SummaryCard = ({ title, value, color, icon }) => (
    <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flex: 1,
        minWidth: '200px',
        borderLeft: `4px solid ${color}`
    }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>{title}</span>
            <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: `${color}15`, color: color }}>
                {icon}
            </div>
        </div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>
            ₹{value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
        </div>
    </div>
);

const AccountsInvoices = ({ db }) => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [salesFilter, setSalesFilter] = useState('all');
    const [firmFilter, setFirmFilter] = useState('all');
    const [firms, setFirms] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    
    const [showInvModal, setShowInvModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    
    const [showPayModal, setShowPayModal] = useState(false);
    const [invoiceForPayment, setInvoiceForPayment] = useState(null);

    const [showPaymentsDetailModal, setShowPaymentsDetailModal] = useState(false);
    const [selectedInvoiceForPaymentsDetail, setSelectedInvoiceForPaymentsDetail] = useState(null);

    const location = useLocation();
    const rowRefs = useRef({});
    const [highlightedRowId, setHighlightedRowId] = useState(null);

    useEffect(() => {
        setLoading(true);
        const q = query(collection(db, "accounts_invoices"), orderBy("createdAt", "desc"));
        
        const unsubscribeInvoices = onSnapshot(q, (snapshot) => {
            setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (error) => {
            console.error("Invoices listener error:", error);
            setLoading(false);
        });

        // Fetch firms
        getDocs(collection(db, "firms")).then(firmSnap => {
            setFirms(firmSnap.docs.map(doc => ({ id: doc.id, name: doc.data().name })));
        });

        return () => unsubscribeInvoices();
    }, [db]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const highlightId = queryParams.get('highlight');

        if (highlightId && invoices.length > 0) {
            const rowElement = rowRefs.current[highlightId];
            if (rowElement) {
                rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setHighlightedRowId(highlightId);

                const timer = setTimeout(() => {
                    setHighlightedRowId(null);
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.delete('highlight');
                    window.history.replaceState({}, '', newUrl.toString());
                }, 3000);

                return () => clearTimeout(timer);
            }
        }
    }, [invoices, location.search]);

    const filteredInvoices = invoices.filter(inv => {
        const matchesSearch = inv.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             inv.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase());
        
        let matchesStatus = true;
        if (statusFilter !== 'all') {
            if (statusFilter === 'overdue') {
                const today = new Date();
                today.setHours(0,0,0,0);
                const dueDate = new Date(inv.dueDate);
                dueDate.setHours(0,0,0,0);
                matchesStatus = inv.balance > 0 && dueDate < today;
            } else {
                matchesStatus = inv.status === statusFilter;
            }
        }

        const matchesSales = salesFilter === 'all' || inv.salesPerson === salesFilter;
        const matchesFirm = firmFilter === 'all' || inv.firm === firmFilter;

        const invDate = new Date(inv.issueDate);
        invDate.setHours(0,0,0,0);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        if (start) start.setHours(0,0,0,0);
        if (end) end.setHours(23,59,59,999);

        const matchesDate = (!start || invDate >= start) && (!end || invDate <= end);

        return matchesSearch && matchesStatus && matchesSales && matchesFirm && matchesDate;
    });

    const sortedInvoices = useMemo(() => {
        let sortableItems = [...filteredInvoices];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // Handle string comparison (case-insensitive)
                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();

                // Handle date comparison
                if (sortConfig.key === 'issueDate' || sortConfig.key === 'dueDate') {
                    aVal = new Date(aVal).getTime();
                    bVal = new Date(bVal).getTime();
                }

                if (aVal < bVal) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredInvoices, sortConfig]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown size={12} style={{ marginLeft: '5px', opacity: 0.5 }} />;
        return sortConfig.direction === 'asc' ? 
            <ChevronUp size={12} style={{ marginLeft: '5px' }} /> : 
            <ChevronDown size={12} style={{ marginLeft: '5px' }} />;
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setSalesFilter('all');
        setFirmFilter('all');
        setStartDate('');
        setEndDate('');
        setSortConfig({ key: 'createdAt', direction: 'desc' }); // Reset sort
    };

    const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || salesFilter !== 'all' || firmFilter !== 'all' || startDate !== '' || endDate !== '';

    const applyQuickFilter = (days) => {
        const end = new Date();
        const start = new Date();
        
        if (days === 'thisMonth') {
            start.setDate(1);
        } else if (days === 'lastMonth') {
            start.setMonth(start.getMonth() - 1);
            start.setDate(1);
            const lastMonthEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0); // Corrected logic
            setStartDate(start.toISOString().split('T')[0]);
            setEndDate(lastMonthEnd.toISOString().split('T')[0]);
            return;
        } else {
            start.setDate(start.getDate() - days);
        }
        
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(end.toISOString().split('T')[0]);
    };

    const stats = filteredInvoices.reduce((acc, inv) => {
        acc.total += (inv.totalAmount || 0);
        acc.paid += (inv.amountPaid || 0);
        acc.pending += (inv.balance || 0);
        return acc;
    }, { total: 0, paid: 0, pending: 0 });

    const getStatusStyle = (invoice) => {
        const today = new Date();
        today.setHours(0,0,0,0);
        const dueDate = new Date(invoice.dueDate);
        dueDate.setHours(0,0,0,0);

        const isOverdue = invoice.balance > 0 && dueDate < today;

        if (isOverdue) return { bg: '#fee2e2', text: '#991b1b', label: 'Overdue' };

        switch(invoice.status) {
            case 'paid': return { bg: '#dcfce7', text: '#166534', label: 'Paid' };
            case 'partial': return { bg: '#fef9c3', text: '#854d0e', label: 'Partial' };
            case 'pending': return { bg: '#eff6ff', text: '#2563eb', label: 'Pending' };
            default: return { bg: '#f1f5f9', text: '#475569', label: invoice.status };
        }
    };

    if (loading) return <Spinner />;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <Link to="/accounts" style={{ color: '#64748b' }}><ArrowLeft size={20}/></Link>
                    <h2 style={{ margin: 0 }}>Manage Invoices</h2>
                </div>
            </div>

            {/* Mini Dashboard */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                <SummaryCard title="Total Amount" value={stats.total} color="#2563eb" icon={<FileText size={20}/>} />
                <SummaryCard title="Paid Amount" value={stats.paid} color="#10b981" icon={<CheckCircle size={20}/>} />
                <SummaryCard title="Pending Amount" value={stats.pending} color="#f59e0b" icon={<Clock size={20}/>} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '10px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {[
                        { label: '15 Days', val: 15 },
                        { label: '30 Days', val: 30 },
                        { label: 'This Month', val: 'thisMonth' },
                        { label: 'Last Month', val: 'lastMonth' }
                    ].map(f => (
                        <button
                            key={f.label}
                            onClick={() => applyQuickFilter(f.val)}
                            style={{
                                padding: '6px 12px', borderRadius: '20px', border: '1px solid #e2e8f0',
                                backgroundColor: 'white', color: '#64748b', fontSize: '12px', cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={e => e.target.style.backgroundColor = '#f8fafc'}
                            onMouseOut={e => e.target.style.backgroundColor = 'white'}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Filters */}
            <div style={{ 
                backgroundColor: 'white', padding: '15px', borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '25px',
                display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap'
            }}>
                <div style={{ position: 'relative', flex: '1.5', minWidth: '180px' }}>
                    <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
                    <input 
                        placeholder="Search customer or invoice..." 
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={filterInputStyle}
                    />
                </div>

                <div style={{ position: 'relative', flex: '1', minWidth: '120px' }}>
                    <Building2 style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
                    <select 
                        value={firmFilter}
                        onChange={e => setFirmFilter(e.target.value)}
                        style={{ ...filterInputStyle, paddingLeft: '40px' }}
                    >
                        <option value="all">All Firms</option>
                        {firms.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                    </select>
                </div>

                <div style={{ position: 'relative', flex: '1', minWidth: '120px' }}>
                    <User style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
                    <select 
                        value={salesFilter}
                        onChange={e => setSalesFilter(e.target.value)}
                        style={{ ...filterInputStyle, paddingLeft: '40px' }}
                    >
                        <option value="all">All Sales</option>
                        {SALES_TEAM.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                <div style={{ flex: '0.8', minWidth: '110px' }}>
                    <select 
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        style={{ ...filterInputStyle, paddingLeft: '12px' }}
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="partial">Partial</option>
                        <option value="paid">Paid</option>
                        <option value="overdue">Overdue</option>
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: '1.5', minWidth: '220px' }}>
                    <Calendar size={18} color="#64748b" />
                    <input 
                        type="date" 
                        value={startDate} 
                        onChange={e => setStartDate(e.target.value)}
                        style={{ ...filterInputStyle, paddingLeft: '10px' }}
                        title="Start Date"
                    />
                    <span style={{ color: '#94a3b8' }}>-</span>
                    <input 
                        type="date" 
                        value={endDate} 
                        onChange={e => setEndDate(e.target.value)}
                        style={{ ...filterInputStyle, paddingLeft: '10px' }}
                        title="End Date"
                    />
                </div>
                
                <button 
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    style={{ 
                        display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 12px',
                        backgroundColor: hasActiveFilters ? '#fee2e2' : '#f1f5f9', 
                        border: hasActiveFilters ? '1px solid #fecaca' : 'none',
                        borderRadius: '8px',
                        color: hasActiveFilters ? '#dc2626' : '#94a3b8', 
                        cursor: hasActiveFilters ? 'pointer' : 'default', 
                        fontSize: '13px', fontWeight: '600',
                        transition: 'all 0.2s',
                        flex: 'none'
                    }}
                >
                    <FilterX size={16} /> Clear
                </button>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                    <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e2e8f0' }}>
                        <tr>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('invoiceNo')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Invoice No. {getSortIcon('invoiceNo')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('customerName')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Customer {getSortIcon('customerName')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('totalAmount')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Total {getSortIcon('totalAmount')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('amountPaid')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Paid {getSortIcon('amountPaid')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('balance')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Balance {getSortIcon('balance')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('issueDate')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Issue Date {getSortIcon('issueDate')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('dueDate')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Due Date {getSortIcon('dueDate')}</div>
                            </th>
                            <th style={{...thStyle, cursor: 'pointer'}} onClick={() => requestSort('status')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>Status {getSortIcon('status')}</div>
                            </th>
                            <th style={{...thStyle, textAlign: 'center'}}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedInvoices.map(inv => {
                            const status = getStatusStyle(inv);
                            const isOverdue = inv.balance > 0 && new Date(inv.dueDate) < new Date();
                            const rowBg = isOverdue ? '#fff8f8' : 'white';

                            return (
                                <tr 
                                    key={inv.id} 
                                    ref={el => rowRefs.current[inv.id] = el}
                                    style={{ 
                                        borderBottom: '1px solid #f1f5f9', 
                                        backgroundColor: inv.id === highlightedRowId ? '#e6f7ff' : rowBg,
                                        transition: 'background-color 0.5s ease'
                                    }}
                                >
                                    <td style={tdStyle}>
                                        <div style={{ fontWeight: 'bold' }}>{inv.invoiceNo}</div>
                                        <div style={{ fontSize: '11px', color: '#64748b' }}>{inv.salesPerson}</div>
                                    </td>
                                    <td style={tdStyle}>{inv.customerName}</td>
                                    <td style={tdStyle}>₹{inv.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                    <td style={{...tdStyle, color: '#059669'}}>₹{inv.amountPaid.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                    <td style={{...tdStyle, fontWeight: 'bold', color: inv.balance > 0 ? '#dc2626' : '#059669'}}>₹{inv.balance.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                    <td style={tdStyle}>{new Date(inv.issueDate).toLocaleDateString()}</td>
                                    <td style={tdStyle}>{new Date(inv.dueDate).toLocaleDateString()}</td>
                                    <td style={tdStyle}>
                                        <span style={{ 
                                            padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', height: 'fit-content',
                                            backgroundColor: status.bg, color: status.text
                                        }}>
                                            {status.label}
                                        </span>
                                    </td>
                                    <td style={{...tdStyle, textAlign: 'center'}}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                            {inv.balance > 0 && (
                                                <button 
                                                    onClick={() => { setInvoiceForPayment(inv); setShowPayModal(true); }}
                                                    style={{ 
                                                        backgroundColor: '#2563eb', color: 'white', border: 'none', 
                                                        padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
                                                        display: 'flex', alignItems: 'center', gap: '4px'
                                                    }}
                                                    title="Record Payment"
                                                >
                                                    <CreditCard size={12} /> Pay
                                                </button>
                                            )}
                                            <button 
                                                onClick={() => { setSelectedInvoice(inv); setShowInvModal(true); }}
                                                style={{ 
                                                    padding: '6px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', 
                                                    backgroundColor: 'white', cursor: 'pointer' 
                                                }}
                                                title="Edit Invoice"
                                            >
                                                <Edit2 size={12} color="#64748b" />
                                            </button>
                                            <button 
                                                onClick={() => { setSelectedInvoiceForPaymentsDetail(inv); setShowPaymentsDetailModal(true); }}
                                                style={{ 
                                                    padding: '6px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', 
                                                    backgroundColor: 'white', cursor: 'pointer' 
                                                }}
                                                title="View Payments History"
                                            >
                                                <History size={12} color="#64748b" />
                                            </button>
                                            <button 
                                                onClick={async () => {
                                                    if(window.confirm("Are you sure you want to delete this invoice?")) {
                                                        await deleteDoc(doc(db, "accounts_invoices", inv.id));
                                                    }
                                                }}
                                                style={{ 
                                                    padding: '6px 8px', borderRadius: '6px', border: '1px solid #fee2e2', 
                                                    backgroundColor: 'white', cursor: 'pointer' 
                                                }}
                                                title="Delete Invoice"
                                            >
                                                <Trash2 size={12} color="#ef4444" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {filteredInvoices.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No invoices found matching your filters.</div>
                )}
            </div>

            <AccountsInvoiceModal 
                show={showInvModal} 
                onClose={() => setShowInvModal(false)} 
                onSave={() => setShowInvModal(false)} 
                db={db} 
                invoiceData={selectedInvoice}
            />

            <PaymentModal 
                show={showPayModal} 
                onClose={() => setShowPayModal(false)} 
                onSave={() => setShowPayModal(false)} 
                db={db} 
                invoice={invoiceForPayment}
            />

            {/* New: Invoice Payments Detail Modal */}
            <InvoicePaymentsDetailModal
                show={showPaymentsDetailModal}
                onClose={() => setShowPaymentsDetailModal(false)}
                invoice={selectedInvoiceForPaymentsDetail}
                db={db}
            />
        </div>
    );
};

// --- STYLES ---
const filterInputStyle = { padding: '10px 12px', paddingLeft: '40px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', backgroundColor: '#fff', fontSize: '14px', width: '100%', boxSizing: 'border-box' };
const thStyle = { padding: '12px 15px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 15px', fontSize: '13px', verticalAlign: 'middle' };

export default AccountsInvoices;
